import {
  AgGridColFactoryService,
  PPSSTabName,
} from '@products/fund-listing/services/ag-grid-col-factory.service';
import {
  CalcTypeComponent,
  CalcTypeGroupHeaderComponent,
  CalcTypeGroupHeaderParams,
} from '@products/fund-listing/ag-grid-helpers/group-headers/calc-type-group-header/calc-type-group-header.component';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ColDef, ColGroupDef, CellKeyDownEvent } from '@ag-grid-community/core';
import {
  ConfigurationId,
  PPSSState,
  PerformanceViewOption,
  ProductData,
  ToggleProps,
  FundShareClassId,
  PPSSGridContext,
} from '@types';
import {
  PerformanceComponent,
  PerformanceGroupHeaderComponent,
  PerformanceGroupHeaderParams,
} from '@products/fund-listing/ag-grid-helpers/group-headers/performance-group-header/performance-group-header.component';
import { Subject, Subscription } from 'rxjs';

import { AgGridAngular } from '@ag-grid-community/angular';
import { AgGridHelperService } from '@products/fund-listing/services/ag-grid-helper.service';
import { DateGroupHeaderComponent } from '@products/fund-listing/ag-grid-helpers/group-headers/date-group-header/date-group-header.component';
import { ExpenseRatioCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/expense-ratio-cell-renderer/expense-ratio-cell-renderer.component';
import { FundDocumentCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fund-document-cell-renderer/fund-document-cell-renderer.component';
import { FundNameCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fund-name-cell-renderer/fund-name-cell-renderer.component';
import { PpssCardContainerComponent } from '@products/fund-listing/ppss-card-container/ppss-card-container.component';
import { PpssService } from '../../services/ppss.service';
import { PpssTableBase } from '../../ppss-table-base';
import { RatingsCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/ratings-cell-renderer/ratings-cell-renderer.component';
import { TranslateService } from '@shared/translate/translate.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { Logger } from '@utils/logger';
import { PpssServiceFactory } from '@products/fund-listing/services/ppss.service.factory';
import { FavFundCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fav-fund-cell-renderer/fav-fund-cell-renderer.component';

import { ProfileService } from '@services/profile.service';
import { SiteConfigService } from '@services';
import { FundNameNjBestCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fund-name-njbest-cell-renderer/fund-name-njbest-cell-renderer.component';
import { AgGridService } from '@frk/eds-components';
import { DatadogService } from '@services/datadog.service';

const logger = Logger.getLogger('PpssPerformanceComponent');

interface PpssPerformanceMonitor {
  initial?: number;
  productType: string;
  processingData: number[];
  tableDataRendered?: number;
  callingServices?: number;
  dataRequests?: number;
  firstDataReturned?: number;
  lastDataReturned?: number;
}
@Component({
  selector: 'ft-ppss-performance',
  templateUrl: './ppss-performance.component.html',
  styleUrls: ['./ppss-performance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpssPerformanceComponent
  extends PpssTableBase
  implements
    OnInit,
    OnChanges,
    OnDestroy,
    CalcTypeComponent,
    PerformanceComponent {
  @Input() isHandheld = false;
  @Input() active;
  isSiteIntl: boolean;
  performanceView: PerformanceViewOption;
  performanceViewOptions: PerformanceViewOption[];
  navAsOfDate: string;
  performanceAsOf: string;
  showPerformanceViewToggle: boolean;
  periodEnd: string;
  productType: ConfigurationId;
  currentPage: ProductData[];
  calcTypeParams: CalcTypeGroupHeaderParams;
  numberofFilteredData: number;
  performanceParams: PerformanceGroupHeaderParams;
  private perfMon: PpssPerformanceMonitor;

  // SA specific variables.
  showPerformanceCurrency = true;
  performanceCurrencyOptions: string[] = [];
  selectedPerformanceCurrencyOption: string;

  private ppssSubscription: Subscription;
  private unsubscribe$: Subject<void> = new Subject<void>();

  // AG-Grid specific variables.
  columnDefs: ColGroupDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
  };
  gridContext: PPSSGridContext = {
    filteredRows: [],
    totalPages: 0,
    currentPage: 0,
    pageSize: 0,
    // NGC-13646: SA - Performance currency calculation (to be consumed in 'getPerformanceData()' in 'AgGridHelperService')
    // Intentionally done here, to have minimal change and to avoid updating each and every "performance coldef method"
    // in every "coldef file".
    selectedPerformanceCurrency: null,
  };
  frameworkComponents = {
    performanceGroupHeaderComponent: PerformanceGroupHeaderComponent,
    dateGroupHeaderComponent: DateGroupHeaderComponent,
    calcTypeGroupHeaderComponent: CalcTypeGroupHeaderComponent,
    ratingsCellRenderComponent: RatingsCellRendererComponent,
    fundDocumentCellRendererComponent: FundDocumentCellRendererComponent,
    fundNameCellRendererComponent: FundNameCellRendererComponent,
    fundNameNjBestCellRendererComponent: FundNameNjBestCellRendererComponent,
    expenseRatioCellRendererComponent: ExpenseRatioCellRendererComponent,
    favFundCellRendererComponent: FavFundCellRendererComponent,
  };
  discreteMonthlyLabels: object;
  discreteQuarterlyLabels: object;
  calendarYearMonthlyLabels: object;
  calendarYearQuarterlyLabels: object;
  calcType: ToggleProps | string;

  @ViewChild('performanceGrid', { static: false, read: AgGridAngular })
  public performanceGrid: AgGridAngular;
  @ViewChild('performanceGrid', { static: false, read: ElementRef })
  performanceEl: ElementRef;
  @ViewChild('performanceCards', { static: false })
  performanceCards: PpssCardContainerComponent;
  private ppssService: PpssService;

  constructor(
    private cdr: ChangeDetectorRef,
    ppssServiceFactory: PpssServiceFactory,
    private agGridHelperService: AgGridHelperService,
    private agGridColFactoryService: AgGridColFactoryService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private siteConfigService: SiteConfigService,
    private agGridService: AgGridService,
    private datadogService: DatadogService
  ) {
    super();
    this.ppssService = ppssServiceFactory.getPpssService();
    this.productType = this.ppssService.getProductType();
    this.perfMon = {
      initial: Date.now(),
      productType: this.productType,
      processingData: [],
    };

    if (this.agGridHelperService.calcTypeConfiguration) {
      this.calcType = this.agGridHelperService.calcTypeConfiguration[
        this.productType
      ];
    }
  }

  ngOnInit() {
    if (this.ppssService) {
      this.perfMon.callingServices = Date.now() - this.perfMon.initial;
      this.ppssSubscription = this.ppssService.subscribe((state: PPSSState) =>
        this.mapState(state)
      );
      this.handlePagination();
    }
  }

  public onGridReady(): void {
    this.agGridService.registerGrid(this.performanceGrid, this.performanceEl);
    logger.debug('ppss grid ready');
  }

  public onGridFirstDataRendered(e): void {
    this.perfMon.tableDataRendered = Date.now() - this.perfMon.initial;
    this.perfMon.dataRequests = this.perfMon.processingData.length;
    this.perfMon.firstDataReturned = this.perfMon.processingData[0];
    this.perfMon.lastDataReturned = this.perfMon.processingData[
      this.perfMon.dataRequests - 1
    ];
    logger.debug('ppss', this.perfMon);
    delete this.perfMon.processingData;
    delete this.perfMon.initial;
    this.datadogService.addAction('ppss', this.perfMon);
  }

  /**
   * Handling Pagination.
   */
  private handlePagination() {
    this.ppssService.getNumberOfPages$().subscribe((totalNumberOfPages) => {
      this.gridContext.totalPages = totalNumberOfPages;
      this.gridContext.pageSize = this.ppssService.getPageSize();

      this.performanceGrid?.api?.paginationSetPageSize(
        Number(this.ppssService.getPageSize())
      );
      this.cdr.detectChanges();
    });
    this.ppssService
      .getCurrentPage$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((pageData: ProductData[]): void => {
        this.currentPage = pageData;
        this.cdr.detectChanges();
      });
    this.ppssService
      .getPageNumber$()
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((n: number): void => {
        this.gridContext.currentPage = n;
        this.gridContext.pageSize = this.ppssService.getPageSize();

        this.performanceGrid?.api?.paginationGoToPage(n - 1);

        if (!this.ppssService._isFilterProcessing) {
          const firstRowIndex =
            this.performanceGrid?.api?.paginationGetPageSize() * (n - 1);
          this.performanceGrid?.api?.setFocusedCell(firstRowIndex, 'FUND-NAME');
        }
        this.redrawGrid(this);
      });
  }

  ngOnDestroy() {
    this.agGridService.removeGrid(this.performanceGrid);
    this.ppssSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  redrawGrid(ref) {
    logger.debug('redrawing performance grid');
    ref.performanceGrid?.api?.redrawRows();
  }
  ngOnChanges(changes: SimpleChanges) {
    // need to explicitly run detectChanges() when isHandheld changes, because component has been detached from change detection earlier
    if (changes.isHandheld) {
      this.cdr.detectChanges();
    }
    if (this.active) {
      const that = this;
      setTimeout(() => {
        that.redrawGrid(that);
      }, 1000);
    }
  }
  setAccessibility(event: CellKeyDownEvent) {
    this.ppssService.setAccessibility(event.colDef.colId, event.event);
  }
  /**
   * Called when the state changes.
   * @param state initial/refreshed state.
   */
  mapState(state: PPSSState) {
    if (state.lastAction === 'populate' || this.firstTime) {
      if (state.data.tableRows.length && this.perfMon.initial) {
        this.perfMon.processingData.push(Date.now() - this.perfMon.initial);
      }
      this.gridContext.filteredRows = state.data.filteredTableRows;
      this.onPopulate(state);
    }
    if (state.lastAction === 'performanceView') {
      this.setPerfGroupColumnsVisibility(state);
    }
    if (state.lastAction === 'changeCalcTab') {
      this.setSelectedCalcType(state);
    }
    if (state.lastAction === 'filterRows' || this.firstTime) {
      this.numberofFilteredData = state.data.filteredTableRows.length;
      this.cdr.detectChanges();
      this.gridContext.filteredRows = state.data.filteredTableRows;
      this.onPopulate(state);
      this.performanceGrid?.api.onFilterChanged();
      this.performanceGrid?.api.resetRowHeights();
    }

    if (state.lastAction === 'updateRows') {
      this.performanceGrid?.api.resetRowHeights();
    }

    this.firstTime = false;

    state.isNjBest = this.siteConfigService.isNjBest();
  }

  isExternalFilterPresent = () => true;

  doesExternalFilterPass(node: any) {
    if (node.gridOptionsWrapper.getContext().filteredRows) {
      return (
        node.gridOptionsWrapper.getContext().filteredRows.indexOf(node.data) >
        -1
      );
    }
    return true;
  }

  /**
   * Called on initial population of the data and config.
   * Set config details and define column defs.
   * @param state initial state.
   */
  onPopulate(state: PPSSState) {
    super.onPopulate(state);

    if (!this.isPopulated) {
      return;
    }

    this.isSiteIntl = state.isSiteIntl;
    this.navAsOfDate = state.data.asOfDates.navAsOfDate;
    if (state.config.calcType) {
      if (typeof this.calcType === 'string') {
        this.calcType = state.config.calcType;
      } else {
        (this.calcType as ToggleProps).selected = state.config.calcType;
      }
    }

    this.performanceViewOptions = state.config.performanceViewOptions;

    // NGC-13646: SA performance currency options.
    this.showPerformanceCurrency = state.config.showPerformanceCurrency;

    if (this.showPerformanceCurrency) {
      this.performanceCurrencyOptions = state.config.performanceCurrencyOptions;
      this.selectedPerformanceCurrencyOption =
        state.config.selectedPerformanceCurrencyOption;
      this.gridContext.selectedPerformanceCurrency = this.selectedPerformanceCurrencyOption;
    }

    // LOGIC: NGC-1351.
    // Create column labels dynamically for performance types - 'CALENDAR_YEAR' & 'DISCRETE'.
    // Save the values and pass it to the helper methods to display it into header name.
    const perfAsOfDates = state.data.asOfDates;
    this.discreteMonthlyLabels = this.agGridHelperService.createColumnLabels(
      'DISCRETE',
      perfAsOfDates.performanceAsOfDateMonthlyStd,
      state.siteConfig.language
    );
    this.discreteQuarterlyLabels = this.agGridHelperService.createColumnLabels(
      'DISCRETE',
      perfAsOfDates.performanceAsOfDateQuarterlyStd,
      state.siteConfig.language
    );
    this.calendarYearMonthlyLabels = this.agGridHelperService.createColumnLabels(
      'CALENDAR_YEAR',
      perfAsOfDates.performanceAsOfDateMonthlyStd,
      state.siteConfig.language
    );
    this.calendarYearQuarterlyLabels = this.agGridHelperService.createColumnLabels(
      'CALENDAR_YEAR',
      perfAsOfDates.performanceAsOfDateQuarterlyStd,
      state.siteConfig.language
    );

    // Fetch the column definitions from factory accordingly.
    this.columnDefs = (this.agGridColFactoryService.generateColDefs(
      PPSSTabName.PERFORMANCE,
      this,
      state,
      this.agGridHelperService,
      this.translateService
    ) as ColGroupDef[]).filter((group) => {
      if (group.groupId === 'PERFORMANCE') {
        this.filterPerformanceColumns(group);
        return true;
      }
      return true;
    });

    if (state.config.hideNavChange) {
      this.columnDefs.forEach((group: ColGroupDef) => {
        if (group.groupId === 'NAV') {
          group.children = group.children.filter((coldDef: ColDef) => {
            return coldDef.colId !== 'NAVCHANGEVALUE';
          });
        }
      });
    }
    if (state.config.hideFactsheet) {
      this.columnDefs.forEach((group: ColGroupDef) => {
        if (group.groupId === 'PERFORMANCE') {
          group.children = group.children.filter((coldDef: ColDef) => {
            return coldDef.colId !== 'FUND-DOCUMENT';
          });
        } else if (
          state.siteConfig?.country === 'BR' &&
          group.groupId === 'AUM'
        ) {
          group.children = group.children.filter((coldDef: ColDef) => {
            return coldDef.colId !== 'FUND-DOCUMENT';
          });
        }
      });
    }

    if (
      this.productType === ConfigurationId.VIP &&
      !this.profileService.isLoggedIn()
    ) {
      this.columnDefs.forEach((group) => {
        if (group.groupId === 'NAV') {
          group.children.splice(1, 2);
        }
        if (group.groupId === 'PERFORMANCE') {
          group.children.splice(0, 10);
          group.children.splice(1, 1);
        }
      });
    }

    this.setNavGroupColumnsVisibility();
    this.setPerfGroupColumnsVisibility(state);
    this.setSelectedCalcType(state);
    logger.debug('updating grid');
    this.cdr.detectChanges();
  }

  /**
   * Called when performance view options change
   * @param state refreshed state.
   */
  setPerfGroupColumnsVisibility(state: PPSSState) {
    this.performanceView = state.config.performanceView;
    this.periodEnd = state.config.periodEnd;

    if (this.showPerformanceCurrency) {
      this.selectedPerformanceCurrencyOption =
        state.config.selectedPerformanceCurrencyOption;
      this.gridContext.selectedPerformanceCurrency = this.selectedPerformanceCurrencyOption;
    }

    if (this.periodEnd === 'MONTHLY') {
      this.performanceAsOf = state.data.asOfDates.performanceAsOfDateMonthly;
    } else {
      this.performanceAsOf = state.data.asOfDates.performanceAsOfDateQuarterly;
    }

    let columns: ColDef[] = this.showPerformanceColumns();
    columns = this.showAdditionalPerformanceColumns(columns);

    this.performanceGrid?.gridOptions?.api?.refreshHeader();
    this.performanceCards?.refresh();
    this.performanceParams = { ...this.performanceParams }; // hack to force params to redraw to pick up performanceAsOf changes
    this.cdr.detectChanges();
  }

  private showPerformanceColumns() {
    // LOGIC: NGC-1351.
    // For intl site, the inception column without M/Q suffixes should get displayed.
    // This is for perf-type as 'CALENDAR_YEAR' and 'DISCRETE'.
    const inceptionCol = this.performanceView
      ? `${this.performanceView.name}-INCEPTION`
      : this.agGridHelperService.getCalendarYearInceptionColDef().colId;
    const additionColumnsInPerfGroup = [
      inceptionCol,
      this.agGridHelperService.getFundDocumentColDef().colId,
      this.agGridHelperService.getExpenseRatioColDef().colId,
      this.agGridHelperService.getTotalAnnualOperatingExpensesColDef().colId,
    ];

    let columns: ColDef[];
    const perfGroup = this.columnDefs.find(
      (group) => group.groupId === 'PERFORMANCE'
    );
    const currentPerf = `${this.performanceView?.name}-${this.periodEnd}`;
    columns = perfGroup.children;
    this.showColumns(
      columns,
      (col) =>
        col.colId.startsWith(currentPerf) ||
        additionColumnsInPerfGroup.includes(col.colId)
    );
    return columns;
  }

  private showAdditionalPerformanceColumns(columns: ColDef[]) {
    const additionalPerfGroup = this.columnDefs.find(
      (group) => group.groupId === 'ADDITIONAL_PERFORMANCE'
    );
    const additionalPerf = `-${this.periodEnd}-`;
    if (additionalPerfGroup) {
      columns = additionalPerfGroup.children;
      this.showColumns(
        columns,
        (col) => col.colId.indexOf(additionalPerf) > -1
      );
    }
    return columns;
  }

  private showColumns(columns: ColDef[], predicate: (col: ColDef) => boolean) {
    const hideCols = columns
      .filter((col) => !predicate(col))
      .map((col) => {
        col.hide = true;
        return col.colId;
      });
    const showCols = columns
      .filter((col) => predicate(col))
      .map((col) => {
        col.hide = false;
        return col.colId;
      });

    if (this.performanceGrid) {
      this.performanceGrid.columnApi.setColumnsVisible(
        this.showPerformanceCurrency ? [...hideCols, ...showCols] : hideCols,
        false
      );
      this.performanceGrid.columnApi.setColumnsVisible(showCols, true);
    }
  }

  /**
   * Set the calcType as per the toggle done.
   * @param state refreshed state.
   */
  private setSelectedCalcType(state: PPSSState) {
    if (state.config.calcType) {
      if (typeof this.calcType === 'string') {
        this.calcType = state.config.calcType;
      } else {
        (this.calcType as ToggleProps).selected = state.config.calcType;
      }
    }

    this.setNavGroupColumnsVisibility();

    this.performanceGrid?.gridOptions?.api?.refreshCells({
      columns: this.performanceGrid.columnApi
        .getColumnGroup('PERFORMANCE')
        .getLeafColumns(),
    });

    if (
      this.performanceGrid?.columnApi.getColumnGroup('ADDITIONAL_PERFORMANCE')
    ) {
      this.performanceGrid?.gridOptions?.api?.refreshCells({
        columns: this.performanceGrid.columnApi
          .getColumnGroup('ADDITIONAL_PERFORMANCE')
          .getLeafColumns(),
      });
    }

    this.performanceGrid?.gridOptions?.api?.refreshHeader();
    this.performanceCards?.refresh();
    this.cdr.detectChanges();
  }

  /**
   * Set the column visibility for NAV group cols based upon calc type selected.
   */
  setNavGroupColumnsVisibility() {
    if (
      this.ppssService.isSettingNavGroupColumnsVisibilityEnabled() &&
      (this.productType === ConfigurationId.CEF ||
        this.productType === ConfigurationId.ETF)
    ) {
      const calcTypeValue =
        typeof this.calcType === 'string'
          ? this.calcType
          : (this.calcType as ToggleProps).selected;

      const navColDefs = this.columnDefs.find(
        (group) => group.groupId === 'NAV'
      ).children;

      if (calcTypeValue === 'NAV') {
        this.showColumns(
          navColDefs,
          (col) =>
            col.colId.startsWith('NAV') ||
            col.colId === 'DAILY-VOLUME' ||
            col.colId === 'PREMIUM-DISCOUNT'
        );
      } else if (calcTypeValue === 'MARKET_PRICE') {
        this.showColumns(
          navColDefs,
          (col) =>
            col.colId.startsWith('MARKETPRICE') ||
            col.colId === 'DAILY-VOLUME' ||
            col.colId === 'PREMIUM-DISCOUNT'
        );
      }
    }
  }

  /**
   * Filter performance columns based upon performance view options (name).
   * @param group column group definition
   */
  private filterPerformanceColumns(group: ColGroupDef) {
    let columns: ColDef[];
    columns = group.children;
    group.children = columns.filter((col) => {
      if (
        col.colId.startsWith('AVG_ANNUAL') &&
        !this.config.performanceViewOptions.find(
          (perfView) => perfView.name === 'AVG_ANNUAL'
        )
      ) {
        return false;
      }
      if (
        col.colId.startsWith('CALENDAR_YEAR') &&
        !this.config.performanceViewOptions.find(
          (perfView) => perfView.name === 'CALENDAR_YEAR'
        ) &&
        !this.showColIfNoPerformanceConfigured(col)
      ) {
        return false;
      }
      if (
        col.colId.startsWith('CUMULATIVE') &&
        !this.config.performanceViewOptions.find(
          (perfView) => perfView.name === 'CUMULATIVE'
        )
      ) {
        return false;
      }
      if (
        col.colId.startsWith('DISCRETE') &&
        !this.config.performanceViewOptions.find(
          (perfView) => perfView.name === 'DISCRETE'
        )
      ) {
        return false;
      }

      // Below condition will cover the 'PERFORMANCE-INCEPTION-DATE' column as well for intl case.
      // And it should be covered, as this column will be independent to performance-view/period-end values.
      return true;
    });
  }

  private showColIfNoPerformanceConfigured(col: ColDef) {
    return (
      this.config.performanceViewOptions.length === 0 &&
      col.colId ===
        this.agGridHelperService.getCalendarYearInceptionColDef().colId
    );
  }

  /**
   * Update performance view when period end value is changed.
   * @param view performance view
   * @param periodEnd selected period end value
   * @param performanceCurrency selected performance currency
   */
  updatePerformanceView(
    view: PerformanceViewOption,
    periodEnd: string,
    performanceCurrency?: string
  ) {
    this.ppssService.changePerformanceView(
      view,
      periodEnd,
      performanceCurrency
    );
  }

  updateFavourite(fund: FundShareClassId) {
    this.ppssService.updateFavourite(fund);
  }

  /**
   * Update the nav change value to the state.
   * @param value the switch value. Ex. on/off
   */
  updateCalcType(selection: string): void {
    this.ppssService.changeCalcTab(selection);
  }

  // change detection on sorting
  onSortChanged = () => this.cdr.detectChanges();
}
